import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import omitBy from 'lodash/omitBy';
import isPlainObject from 'lodash/isPlainObject';
import camelCase from 'lodash/camelCase';
import round from 'lodash/round';

import {reporter} from 'common/report/error-reporting';
import {RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';

import './immer';

// TODO please remove these generic imports from callers as bandwidth is available; prefer "import {foo} from 'common/util/array';"
export {
  SORT_ORDERING,
  baseComparator,
  boolComparator,
  dateStringComparator,
  headerComparator,
  immutableSplice,
  insertAtIndex,
  moveByIndex,
  numDateAlphaComparator,
  numericComparator,
  rangeComparator,
  removeByIndex,
  removeByValue,
  replaceByIndex,
  sorted,
  unique,
} from './array';

// TODO please remove these generic imports from callers as bandwidth is available; prefer "import {foo} from 'common/util/string';"
export {capitalize} from './string';

export const MeasureUtil = {
  measureTextWidth,
};

const IGNORED_RESULT_SERIES_KEYS = new Set([RESULTS_OVERALL_SUM_KEY]);

/**
 * Gets a property from an object ONLY if it's a direct property of the
 * object, not a property inherited from the object's prototype chain.
 *
 * Note: Named "getOwnAttr" instead of "getOwnProperty" to avoid confusing
 *       name collisions in app code referring to Mixpanel properties.
 *
 * Note: Don't use `obj.hasOwnProperty` directly; this does not account for the case
 *       where a user-provided object key (e.g. event/prop name) IS "hasOwnProperty".
 *       Also, prototype-less objects may be created via `Object.create(null)` which
 *       will not have an `obj.hasOwnProperty` method.
 *
 * @param {object} obj
 * @param {string} attr
 * @return {any} the property value if direct property of the object; null otherwise
 */
export function getOwnAttr<Type = any>(obj: Obj, attr: string): Type | null {
  if (isNil(obj)) {
    return null;
  }

  return Object.prototype.hasOwnProperty.call(obj, attr) ? obj[attr] : null;
}

/**
 * Trims trailing and leading zeroes from a number string
 *
 * @param {string} value - the number to abbreviate
 * @return {string} - formatted string without trailing or leading zeroes
 */
export function trimZeroesNumber(value: string): string {
  let [int, decimal = ``] = value.split(`.`);
  int = int.replace(/^0+/g, ``) || `0`;
  decimal = decimal.replace(/0+$/g, ``);
  decimal = decimal.length > 0 ? `.${decimal}` : ``;
  return `${int}${decimal}`;
}

// generic data-manipulation utils

/**
 * Turns a number into nicely formatted USD, e.g. 1,000 -> $1,000.00
 *
 * @param {number|string} dollarAmt - the number to turn into USD
 * @return {string} - number in USD
 */
export function numberInUSD(
  dollarAmt: number | string,
  options?: {maximumFractionDigits?: number; minimumFractionDigits?: number},
): string {
  return dollarAmt.toLocaleString(`en-US`, {currency: `USD`, style: `currency`, ...options});
}

export function commaizeNumber(
  value: number | string,
  {
    maxPrecision = null,
    exactPrecision = false,
  }: {
    /**
     * Max digits of decimal precision for decimal numbers. Decimals will not be added to integer numbers unless
     * `exactPrecision` is true . Use `null` to not constrain the decimal precision.
     */
    maxPrecision?: number;
    /** Use `maxPrecision` digits of precision, even for integer numbers */
    exactPrecision?: boolean;
  } = {},
): string {
  switch (typeof value) {
    case `number`:
      if (isNaN(value)) {
        return value.toString();
      }
      value = Number.isFinite(maxPrecision) ? value.toFixed(maxPrecision) : value.toString();
      break;
    case `string`:
      // noop
      break;
    default:
      return value;
  }

  let neg = false;
  if (value.startsWith(`-`)) {
    neg = true;
    value = value.slice(1);
  }

  // Parse main number
  const split = value.split(`.`);
  let commaized = parseInt(split[0] || `0`, 10).toString();

  if (commaized.length) {
    commaized = commaized.split(``).reverse().join(``);
    commaized = commaized.match(/.{1,3}/g).join(`,`);
    commaized = commaized.split(``).reverse().join(``);
  }

  if (split[1] && (exactPrecision || parseInt(split[1], 10) !== 0)) {
    // Add decimals, if applicable
    commaized += `.` + split[1];
  }

  if (neg) {
    commaized = `-` + commaized;
  }

  return commaized;
}

/**
 * Returns a number e.g 12345 as an abbreviation e.g 123K
 * @param {number|string} number
 * @param {number} precision - decimal points
 *    e.g 12345 with precision=2 returns 123.45K
 *    e.g 12300 with precision=2 returns 123.00K
 *    e.g 1.234 with precision=2 returns 1.23
 *    e.g. 1 with precision=2 returns 1 (precision does not apply for non-abbreviated integer numbers, so 1 is
 *    returned instead of 1.00)
 * @param {trimZeroes} trimZeroes trim trailing and leading zeroes after converting to @param precision.
 * @param {boolean} spaced - adds a space between suffix for readability e.g 123.45 K
 */
export function abbreviateNumber(
  value: number | string,
  {precision = 1, spaced = false, trimZeroes = false} = {},
): string {
  value = parseFloat(value.toString());

  const largeNumbers = [
    [Math.pow(10, 15), `Q`],
    [Math.pow(10, 12), `T`],
    [Math.pow(10, 9), `B`],
    [Math.pow(10, 6), `M`],
    [Math.pow(10, 3), `K`],
  ];

  let suffix = ``;

  for (let i = 0; i < largeNumbers.length; i++) {
    const bignum = largeNumbers[i][0];
    const letter = largeNumbers[i][1];

    if (Math.abs(value) >= bignum) {
      value /= bignum as number;
      suffix = letter as string;
      break;
    }
  }

  const isNegative = value < 0;
  let fixed: string = value.toFixed(precision);
  if (trimZeroes) {
    fixed = trimZeroesNumber(fixed);
  }
  const fixedArr: string[] = fixed.split(`.`);
  let formatted = commaizeNumber(Math.abs(parseInt(fixedArr[0], 10)));

  if (fixedArr[1] && (suffix || parseInt(fixedArr[1], 10) !== 0)) {
    formatted += `.` + fixedArr[1];
  }

  return (isNegative ? `-` : ``) + formatted + (suffix && spaced ? ` ` : ``) + suffix;
}

/**
 * Abbreviates number with common latin numerical words and then rounds the abbreviated number to n significant figures
 *
 * Note: numbers smaller than 1 are rounded to 3 decimal places because significant figure rounding produces unintuitive strings in these cases
 *
 * @param {number|string} value - the number to abbreviate
 * @param {number} n - number of significant figures
 * @param {boolean} spaced - whether to add a space between the abbreviated number and the suffix
 * @return {string} - locale-formatted string abbreviation of value
 */
function abbreviateNumberNSigFigs(value: number | string, n: number, spaced = false): string {
  value = parseFloat(value.toString());
  const scalar = Math.abs(value);

  if (scalar < 1) {
    const rounded = trimZeroesNumber(value.toFixed(n));
    const tooSmall = parseFloat(rounded) === 0 && value !== 0;
    return tooSmall ? `≈0` : rounded;
  } else {
    // initials of latin numerical words
    const suffixList = [
      [Math.pow(10, 15), `Q`],
      [Math.pow(10, 12), `T`],
      [Math.pow(10, 9), `B`],
      [Math.pow(10, 6), `M`],
      [Math.pow(10, 3), `K`],
      [Math.pow(10, 0), ``],
    ];

    const [factor, suffix] = suffixList.find(([factor]) => scalar >= factor) as [number, string];
    const maybeSpace = spaced && suffix ? ` ` : ``;
    const formattedNumber = trimZeroesNumber((value / factor).toPrecision(n));
    return `${formattedNumber}${maybeSpace}${suffix}`;
  }
}

export function ordinalizeNumber(value: number): string {
  const absNumber = Math.abs(value);
  // numbers ending in 1, 2, or 3 but not in 11, 12, or 13
  if (![11, 12, 13].includes(absNumber % 100) && [1, 2, 3].includes(absNumber % 10)) {
    switch (absNumber % 10) {
      case 1:
        return value + `st`;
      case 2:
        return value + `nd`;
      case 3:
        return value + `rd`;
      default:
        break;
    }
  }
  return value + `th`;
}

/**
 * @deprecated Use the spread operator `{...}` instead, it's functionally the same as
 * `Object.assign` when the first argument to `Object.assign` is an empty object
 *
 * e.g., `extend(this, {clauses})` --> `{...this, clauses}`, or expanded out:
 *
 * `extend(this, {clauses: clauses})` --> `{...this, clauses: clauses}`
 *
 */
export function extend(...args: Obj[]): Obj {
  return Object.assign({}, ...args);
}

export function htmlEncodeString(value: string): string {
  return String(value)
    .replace(/&/g, `&amp;`)
    .replace(/</g, `&lt;`)
    .replace(/>/g, `&gt;`)
    .replace(/"/g, `&quot;`)
    .replace(/'/g, `&#039;`)
    .replace(/\r/g, `&#013;`)
    .replace(/\n/g, `&#010;`);
}

// return object with new keys and same values
export function mapKeys<T>(obj: Obj<T>, func: (key: PropertyKey, val: T) => PropertyKey): Obj<T> {
  return Object.keys(obj).reduce((ret, k) => Object.assign(ret, {[func(k, obj[k])]: obj[k]}), {});
}

// return object with same keys and new values
export function mapValues<T, OutT>(obj: T, func: (val: T[keyof T], key: keyof T) => OutT): Obj<OutT> {
  return Object.keys(obj).reduce((ret, k) => Object.assign(ret, {[k]: func(obj[k], k as keyof T)}), {});
}

/**
 * Gets the first value of the object, but will ignore a key of $overall
 */
export function nestedObjectSansOverall(obj) {
  return obj[Object.keys(obj).find((key) => !IGNORED_RESULT_SERIES_KEYS.has(key))];
}

export function nestedObjectDepth(obj: Obj): number {
  // Ignoring $overall nodes, because, as summary nodes, those tend to be misleadingly shallow.
  return obj !== null && typeof obj === `object` ? nestedObjectDepth(nestedObjectSansOverall(obj)) + 1 : 0;
}

export function parseUrlQueryParams(urlSearch: string = window.location.search): Obj<string> {
  return urlSearch
    .slice(1)
    .split(`&`)
    .reduce((params, pairStr) => {
      const [k, v] = pairStr.split(`=`);
      return Object.assign(params, {[k]: v});
    }, {});
}

function getKeys(obj: Obj, depth: number, keySet: Set<string>): void {
  if (nestedObjectDepth(obj) > depth) {
    Object.keys(obj)
      .filter((key) => !IGNORED_RESULT_SERIES_KEYS.has(key))
      .forEach((key) => getKeys(obj[key], depth, keySet));
  } else {
    Object.keys(obj)
      .filter((key) => !IGNORED_RESULT_SERIES_KEYS.has(key))
      .forEach((key) => keySet.add(key));
  }
}

export function nestedObjectKeys(obj: Obj, depth = 1): PropertyKey[] {
  const keys: Set<string> = new Set();
  getKeys(obj, depth, keys);
  return Array.from(keys);
}

export function camelCaseKeys(obj) {
  if (Array.isArray(obj)) {
    return obj.map((v) => camelCaseKeys(v));
  } else if (isPlainObject(obj)) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [camelCase(key)]: camelCaseKeys(obj[key]),
      }),
      {},
    );
  }
  return obj;
}

export function objToFormData(params: Obj): FormData {
  const formData = new FormData();
  Object.entries(params).forEach(([name, value]) => {
    if (Array.isArray(value)) {
      // FormData does not handle arrays as you'd expect:
      // formData.append('arr', [true, 74, 'John']);
      // formData.get('arr'); -> "[true, 74, 'John']" // WRONG - array converted to string
      // formData.append('arr', true);
      // formData.append('arr', 74);
      // formData.append('arr', 'John');
      // formData.getAll('arr'); -> ['true', '74', 'John'] // CORRECT - array retains type
      value.forEach((item) => formData.append(name, item));
    } else {
      formData.append(name, value);
    }
  });
  return formData;
}

export function objToQueryString(params: Obj): string {
  return Object.keys(params)
    .map((k) => [k, encodeURIComponent(params[k])].join(`=`))
    .join(`&`);
}

export function arrayToQueryString(params: Obj[]): string {
  return params
    .map((param) =>
      Object.keys(param)
        .map((k) => [`${k}[]`, encodeURIComponent(param[k])].join(`=`))
        .join(``),
    )
    .join(`&`);
}

export function sum(arr: number[]): number {
  let _sum = 0;
  for (let i = 0; i < arr.length; i++) {
    _sum += arr[i];
  }
  return _sum;
}

/**
 *  given a monotonically increasing function fn: integer => double,
 *  find the first integer i between start and end inclusive s.t. fn(i) >= 0
 *  or end if fn(end) < 0
 */
export function binarySearch(start: number, end: number, fn: (num: number) => number): number {
  if (fn(start) >= 0) {
    return start;
  }
  if (fn(end) < 0) {
    return end;
  }
  while (end - start > 1) {
    const mid = Math.floor((end + start) / 2);
    if (fn(mid) < 0) {
      start = mid;
    } else {
      end = mid;
    }
  }
  return end;
}

// lazily initialize measureContext, rather than at module top-level,
// so that running tests from node does not cause an undefined
// variable error from document
let measureContext;
function getMeasureContext(): CanvasRenderingContext2D {
  if (!measureContext) {
    const canvas = document.createElement(`canvas`);
    measureContext = canvas.getContext && canvas.getContext(`2d`);
  }
  return measureContext;
}

/**
 * Returns false if `val` is undefined, null or NaN, otherwise true
 * NOTE: Returns true for 0 and '' (empty string)
 * @param val {any}
 * @returns {boolean}
 */
export function isDefinedValue(val: any): boolean {
  if (typeof val === `number`) {
    return !isNaN(val);
  }
  return val !== undefined && val !== null;
}

/* istanbul ignore next */
function measureTextWidth(str: string, font: string): number {
  const measureContext = getMeasureContext();
  if (measureContext) {
    measureContext.font = font;
    return measureContext.measureText(str).width;
  }
  return null;
}

export enum RollingWindowSize {
  Minute = `minute`,
  Hour = `hour`,
  Day = `day`,
  Week = `week`,
  Month = `month`,
  Quarter = `quarter`,
}

//get the rollingcount based on unit for calculating rolling average
export function defaultRollingWindowSize(unit: RollingWindowSize): number | null {
  switch (unit) {
    case RollingWindowSize.Minute:
      return 15;
    case RollingWindowSize.Hour:
      return 12;
    case RollingWindowSize.Day:
      return 7;
    case RollingWindowSize.Week:
      return 5;
    case RollingWindowSize.Month:
      return 3;
    case RollingWindowSize.Quarter:
      return 2;
    default:
      return null;
  }
}

export function filterAttributes<T>(attrs: Obj<T>): Obj<T> {
  return omitBy(attrs, isNil);
}

export function serializeAttributes(attrs: Obj): Obj {
  return mapValues(filterAttributes(attrs), (value) => (isObject(value) ? JSON.stringify(value) : value));
}

export function parseNumber(str: string | number): number {
  if (Number.isFinite(str as number)) {
    return str as number;
  } else if (!str) {
    return null;
  } else {
    const num = Number(str);
    return Number.isNaN(num) ? null : num;
  }
}

/**
 * Generates a predictable anonymous string given
 * a number, e.g. 0 -> A, 1 -> B, 26 -> BA, 98 -> DU
 * 26 ^ 4 -> BAAAA
 * @param {number} index - Index of item to stringify
 */
export function getAlphaLabelFromIndex(index): string {
  if (index < 0) return ``;

  const MIN_CHAR_CODE = `A`.charCodeAt(0);
  const MAX_ALPHA_INDEX = 26; // A-Z
  let str = ``;

  if (index === 0) {
    return `A`;
  }

  const depth = Math.floor(index / 26);
  const mod = index % MAX_ALPHA_INDEX;

  if (depth > 0) {
    str += getAlphaLabelFromIndex(depth);
  }

  return str + String.fromCharCode(MIN_CHAR_CODE + mod);
}

/**
 * Normalizes data group id comparison so that null, undefined and '' are equivalent.
 */
export function dataGroupIDsEqual(a: string | number, b: string | number): boolean {
  return (!a && !b) || a === b;
}

// parseUri 1.2.2
// (c) Steven Levithan <stevenlevithan.com>
// MIT License
export function parseUri(str: string): Obj {
  const o = parseUri.options,
    m = o.parser[o.strictMode ? `strict` : `loose`].exec(str),
    uri = {};
  let i = 14;

  while (i--) {
    uri[o.key[i]] = m[i] || ``;
  }

  uri[o.q.name] = {};
  uri[o.key[12]].replace(o.q.parser, function (_$0, $1, $2) {
    if ($1) {
      uri[o.q.name][$1] = $2;
    }
  });

  return uri;
}

parseUri.options = {
  strictMode: false,
  key: [
    `source`,
    `protocol`,
    `authority`,
    `userInfo`,
    `user`,
    `password`,
    `host`,
    `port`,
    `relative`,
    `path`,
    `directory`,
    `file`,
    `query`,
    `anchor`,
  ],
  q: {
    name: `queryKey`,
    parser: /(?:^|&)([^&=]*)=?([^&]*)/g,
  },
  parser: {
    strict: /^(?:([^:/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:/?#]*)(?::(\d*))?))?((((?:[^?#/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/,
    loose: /^(?:(?![^:@]+:[^:@/]*@)([^:/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#/]*\.[^?#/.]+(?:[?#]|$)))*\/?)?([^?#/]*))(?:\?([^#]*))?(?:#(.*))?)/,
  },
};

export function replaceQueryParamToFragment({queryKey}: {queryKey: string | undefined}) {
  // This is a hack to get all the fragments when a newly signed up user is redirected directly to demo project/report
  // In fetch redirect, fragments are lost somehow.
  const newUrl = new URL(window.location.href);

  const selectedQueryParam = newUrl.searchParams.get(queryKey);

  newUrl.searchParams.delete(queryKey);

  newUrl.hash = selectedQueryParam;

  window.location.assign(newUrl);
}

// The canonical number formatting/abbreviating function per the Digits Proposal
// https://www.notion.so/mpspeed/Digits-Proposal-b5bf7aa89df8470c937e6c221c379e33
export function formatMetricNumber(num: number): string {
  const MAX_DIGITS = 4;
  const MAX_DECIMAL_DIGITS = 2;

  // round to MAX_DIGITS because decimal precision cannot be higher
  num = round(num, MAX_DIGITS);
  const absoluteNum = Math.abs(num);

  // if we have a 4 digit number, just add commas and round
  if (absoluteNum < 10_000 && absoluteNum >= 1000) {
    return commaizeNumber(num, {maxPrecision: 0});
  }

  const [integer] = absoluteNum.toString().split(`.`);

  // for >5 digit numbers, use our number abbreviation functions
  if (absoluteNum > 9_999) {
    // ensures there is a max of 2 decimals in the abbreviation
    if (integer.length % 3 === 1) {
      return abbreviateNumberNSigFigs(num, 3);
    }

    return abbreviateNumberNSigFigs(num, 4);
  }

  // when number doesn't satisfy previous conditions, we have a special case for floats
  if (!Number.isInteger(absoluteNum)) {
    const numIntDigits = absoluteNum >= 1 ? integer.length : 0;

    const fixed = trimZeroesNumber(num.toPrecision(Math.min(numIntDigits + MAX_DECIMAL_DIGITS, MAX_DIGITS)));

    // chance we could have rounded up to a 4 digit number
    return commaizeNumber(fixed);
  }

  return num.toString();
}

export function formatPercent(decimal: number, precision = 2, exactPrecision = false) {
  if (isNaN(decimal) || !isFinite(decimal)) {
    return `N/A`;
  }
  let value: string | number = Math.round(decimal * Math.pow(10, precision + 2)) / Math.pow(10, precision);
  if (exactPrecision) {
    value = value.toFixed(precision);
  }
  return `${value}%`;
}

export function mapToSerializableObj<T, U>(map: Map<T, U>): {[prop: string]: U} {
  const obj = {};
  for (const [key, value] of map) {
    obj[key.toString()] = value;
  }

  return obj;
}

export function assert(condition: boolean, errorMessage?: string): asserts condition {
  try {
    if (!condition) {
      throw Error([`Assertion failed`, errorMessage].filter(Boolean).join(`: `));
    }
  } catch (err) {
    reporter.warn(err);
  }
}
